import styled from '@emotion/styled';
import { ArrowBackRounded } from '@mui/icons-material';
import { useLocation } from 'wouter';
import { useDocumentTitle } from '~/modules/hooks/useDocumentTitle';
import { liveTvRoutePaths } from '~/modules/live-tv/liveTvRoutePaths';
import LiveTvPlayback from '../components/LiveTvPlayback';
import { useLiveTvChannel } from '../hooks/useLiveTvChannel';
import LiveTvLayout from '../layout/LiveTvLayout';

interface LiveTvChannelPageProps {
  params: {
    channelId: string;
  };
}

export default function LiveTvChannelPage({ params }: LiveTvChannelPageProps) {
  const [, navigate] = useLocation();
  const { channel, loading, error } = useLiveTvChannel(params.channelId);

  useDocumentTitle(channel ? `${channel.name} • fastLiveTv` : 'fastLiveTv');

  const handleBack = () => {
    navigate(liveTvRoutePaths.INDEX);
  };

  return (
    <LiveTvLayout>
      <BackButton type="button" onClick={handleBack}>
        <ArrowBackRounded aria-hidden="true" />
        Back to channels
      </BackButton>

      {loading && <StateMessage>Loading channel...</StateMessage>}
      {error && <StateMessage data-error>{error}</StateMessage>}

      {!loading && !error && channel && (
        <>
          <LiveTvPlayback channel={channel} />

          <ChannelInfo>
            <ChannelName>{channel.name}</ChannelName>
            <MetaList>
              {channel.country && <span>{channel.country}</span>}
              {channel.category && <span>{channel.category}</span>}
              {channel.source && <span>{channel.source}</span>}
              {channel.geoRestriction && <span>{channel.geoRestriction}</span>}
            </MetaList>
          </ChannelInfo>

          {channel.validationStatus && channel.validationStatus !== 'ok' && (
            <ValidationMessage>
              {channel.validationReason || `Validation status: ${channel.validationStatus}`}
            </ValidationMessage>
          )}
        </>
      )}
    </LiveTvLayout>
  );
}

const BackButton = styled.button`
  margin: 0 0 14px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px 0 10px;
  border-radius: 8px;
  border: 1px solid var(--walky-border);
  background: var(--walky-surface);
  color: var(--walky-ink);
  cursor: pointer;
  font-size: 15px;
  font-weight: 750;
  box-shadow: 0 6px 16px rgba(24, 24, 27, 0.06);

  &:hover,
  &:focus-visible {
    border-color: var(--walky-orange);
    color: var(--walky-orange);
    outline: none;
  }
`;

const ChannelInfo = styled.section`
  margin-top: 18px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 2px 12px;
  border-bottom: 1px solid var(--walky-border);
`;

const ChannelName = styled.h1`
  margin: 0;
  color: var(--walky-ink);
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1.1;
`;

const MetaList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  color: var(--walky-muted);
  font-size: 14px;

  span + span::before {
    content: '·';
    margin-right: 16px;
    color: var(--walky-orange);
  }
`;

const ValidationMessage = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-left: 3px solid var(--walky-error-border);
  background: var(--walky-error-bg);
  color: var(--walky-error-text);
  font-size: 14px;
`;

const StateMessage = styled.div`
  padding: 18px;
  border-radius: 8px;
  background: var(--walky-surface);
  border: 1px solid var(--walky-border);
  color: var(--walky-muted);

  &[data-error='true'] {
    color: var(--walky-error-text);
  }
`;
